/**
 * frontend/src/crm/pages/Pipeline.jsx — vue kanban des prospects par étape.
 * Branche v4, handoff « Pipeline commercial » : en-tête à sur-titre, bandeau
 * de 4 indicateurs, colonnes 286px à pastille de couleur, cartes prospect
 * déplaçables. Les couleurs, rayons et états sont ceux de la maquette ; les
 * polices suivent la convention du dépôt (font-heading / font-mono, pilotées
 * par le Studio de thème) plutôt que les DM Sans / DM Mono du prototype.
 *
 * API, routes et data-testid inchangés.
 */
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/crmApi";
import { Loader } from "@/crm/ui";
import { useAuth } from "@/context/CrmAuth";
import { toast } from "sonner";
import { Phone, Mail, Search, Columns3, CalendarDays, Trophy, BarChart3 } from "lucide-react";

// Pastille de tête de colonne, par libellé d'étape. L'ordre des colonnes vient
// de l'API (`data.stages`, piloté par les réglages) — seule la couleur est ici.
const STAGE_DOT = {
  "Nouveau": "#9aa0b2",
  "À qualifier": "#8b5cf6",
  "À contacter": "#3b82f6",
  "Contacté": "#06b6d4",
  "En discussion": "#4a3ae0",
  "Rendez-vous prévu": "#e0a02e",
  "Proposition envoyée": "#e4762e",
  "Négociation": "#c13fb0",
  "À rappeler": "#d0b02a",
  "Gagné": "#2f9e5c",
  "Perdu": "#d64a3a",
};

const scoreStyle = (score) => {
  const n = score ?? 0;
  if (n >= 81) return "bg-[#e6f5ec] text-[#1f7a48]";
  if (n >= 61) return "bg-[#e6f1fb] text-[#1f5f9e]";
  if (n >= 31) return "bg-[#fdf3dd] text-[#8a6b1f]";
  return "bg-[#f1f2f7] text-[#71768a]";
};

export default function Pipeline() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [commercials, setCommercials] = useState([]);
  const [filterComm, setFilterComm] = useState("");
  const [query, setQuery] = useState("");
  const [dragId, setDragId] = useState(null);
  const [over, setOver] = useState(null);

  const load = useCallback(() => {
    const params = filterComm ? { commercial_id: filterComm } : {};
    api.get("/pipeline/board", { params }).then(({ data }) => setData(data)).catch(() => {});
  }, [filterComm]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (user?.role !== "commercial") api.get("/users/commercials").then(({ data }) => setCommercials(data)).catch(() => {}); }, [user]);

  const ownerName = useMemo(() => Object.fromEntries(commercials.map((c) => [c.id, c.name])), [commercials]);

  // Recherche (nom + ville) appliquée avant le calcul des colonnes et des
  // indicateurs ; le filtre commercial, lui, est appliqué par l'API.
  const q = query.trim().toLowerCase();
  const columns = useMemo(() => {
    if (!data) return [];
    return data.stages.map((stage) => {
      const col = data.board[stage] || { items: [], count: 0 };
      const items = q
        ? col.items.filter((c) => `${c.nom || ""} ${c.ville || ""}`.toLowerCase().includes(q))
        : col.items;
      return { stage, items, count: q ? items.length : col.count };
    });
  }, [data, q]);

  const onDrop = async (stage) => {
    if (!dragId) return;
    // optimistic
    setData((d) => {
      const nd = { ...d, board: { ...d.board } };
      let moved;
      for (const st of Object.keys(nd.board)) {
        const idx = nd.board[st].items.findIndex((i) => i.id === dragId);
        if (idx >= 0) { moved = nd.board[st].items[idx]; nd.board[st] = { ...nd.board[st], items: nd.board[st].items.filter((i) => i.id !== dragId), count: nd.board[st].count - 1 }; }
      }
      if (moved) nd.board[stage] = { ...nd.board[stage], items: [moved, ...nd.board[stage].items], count: nd.board[stage].count + 1 };
      return nd;
    });
    const id = dragId; setDragId(null); setOver(null);
    try { await api.patch(`/pipeline/move/${id}`, { status: stage }); }
    catch { toast.error("Échec du déplacement"); load(); }
  };

  if (!data) return <Loader />;

  const won = columns.find((c) => c.stage === "Gagné")?.count || 0;
  const lost = columns.find((c) => c.stage === "Perdu")?.count || 0;
  const total = columns.reduce((n, c) => n + c.count, 0);
  const meetings = columns.find((c) => c.stage === "Rendez-vous prévu")?.count || 0;
  const rate = won + lost ? `${Math.round((won / (won + lost)) * 100)} %` : "—";

  return (
    <div>
      {/* EN-TÊTE DE PAGE */}
      <div className="flex flex-wrap items-end justify-between gap-5 mb-[22px]">
        <div>
          <div className="font-mono text-[11px] tracking-[.14em] uppercase text-[#8a90a3] mb-2">Prospection</div>
          <h1 className="font-heading text-[30px] font-bold tracking-[-.02em] text-[#16181d]">Pipeline commercial</h1>
          <p className="mt-2 text-[14.5px] text-[#61667a] max-w-[56ch] text-pretty">
            Glissez-déposez les prospects entre les étapes. Le statut de l'entreprise est mis à jour immédiatement.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5 max-[640px]:w-full">
          <div className="flex items-center gap-2.5 bg-white border border-[#e2e5ec] rounded-[11px] px-3 py-2 hover:border-[#c9cedb] focus-within:border-[#5b4bff] focus-within:shadow-[0_0_0_3px_rgba(91,75,255,.14)]">
            <Search size={15} className="text-[#9aa0b2] shrink-0" />
            <input data-testid="pipeline-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Filtrer les prospects…"
              className="w-[180px] max-[640px]:w-full border-0 outline-none bg-transparent text-[13.5px]" />
          </div>
          {user?.role !== "commercial" && (
            <select data-testid="pipeline-filter-comm" value={filterComm} onChange={(e) => setFilterComm(e.target.value)}
              className="border border-[#e2e5ec] bg-white rounded-[11px] px-3 py-2.5 text-[13.5px] cursor-pointer hover:border-[#c9cedb]">
              <option value="">Tous commerciaux</option>
              {commercials.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* BANDEAU D'INDICATEURS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 min-[1025px]:grid-cols-4 gap-[14px] mb-5">
        <Kpi label="Prospects en cours" value={total - won - lost} tint="#eeecff" color="#4a3ae0" icon={Columns3} testid="pipeline-kpi-open" />
        <Kpi label="Rendez-vous prévus" value={meetings} tint="#fdf3dd" color="#a37a19" icon={CalendarDays} testid="pipeline-kpi-meetings" />
        <Kpi label="Gagnés" value={won} tint="#e6f5ec" color="#1f7a48" icon={Trophy} testid="pipeline-kpi-won" />
        <Kpi label="Taux de conversion" value={rate} tint="#e6f1fb" color="#1f5f9e" icon={BarChart3} testid="pipeline-kpi-rate" />
      </div>

      {/* KANBAN */}
      <div className="flex items-start gap-[14px] overflow-x-auto pb-[18px] kanban-scroll">
        {columns.map(({ stage, items, count }) => {
          const hot = over === stage;
          const rest = q ? 0 : count - items.length;
          return (
            <section key={stage} data-testid={`pipeline-col-${stage}`}
              onDragOver={(e) => { e.preventDefault(); if (over !== stage) setOver(stage); }}
              onDragLeave={() => setOver((s) => (s === stage ? null : s))}
              onDrop={(e) => { e.preventDefault(); onDrop(stage); }}
              className={`shrink-0 w-[286px] min-h-[460px] rounded-2xl p-3 flex flex-col gap-2.5 border transition-[background-color,border-color] duration-150 ${hot ? "bg-[#eeecff] border-[#b9b1ff]" : "bg-[#eff1f5] border-[#e6e8ee]"}`}>
              <div className="flex items-center gap-2.5">
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: STAGE_DOT[stage] || "#9aa0b2" }} />
                <span className="flex-1 min-w-0 text-[13.5px] font-bold tracking-[-.01em] truncate">{stage}</span>
                <span className="font-mono text-[11.5px] text-[#6f7488] bg-white border border-[#e6e8ee] rounded-full px-2 py-0.5">{count}</span>
              </div>

              {items.map((c) => (
                <article key={c.id} draggable data-testid={`pipeline-card-${c.id}`}
                  onDragStart={() => setDragId(c.id)}
                  onDragEnd={() => { setDragId(null); setOver(null); }}
                  onClick={() => navigate(`/entreprises/${c.id}`)}
                  className={`bg-white border border-[#e6e8ee] rounded-[13px] p-3 flex flex-col gap-[9px] cursor-grab active:cursor-grabbing hover:border-[#c9cedb] hover:shadow-[0_8px_20px_rgba(16,18,28,.08)] ${dragId === c.id ? "opacity-45" : ""}`}>
                  <div className="flex items-start gap-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.nom}</div>
                      <div className="text-xs text-[#8a90a3] mt-[3px] truncate">{c.ville}</div>
                    </div>
                    <span className={`shrink-0 font-mono text-[11.5px] font-medium rounded-[7px] px-[7px] py-[3px] ${scoreStyle(c.lead_score)}`}>
                      {c.lead_score ?? 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="flex items-center gap-[5px]">
                      <Phone size={14} className={c.telephone ? "text-[#2f9e5c]" : "text-[#d3d7e1]"} />
                      <Mail size={14} className={c.email ? "text-[#2f9e5c]" : "text-[#d3d7e1]"} />
                    </span>
                    <span className="flex-1" />
                    <span className="font-mono text-[10.5px] tracking-[.04em] text-[#9aa0b2] truncate">{ownerName[c.commercial_id] || ""}</span>
                  </div>
                  {c.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {c.tags.slice(0, 2).map((t) => (
                        <span key={t} className="font-mono text-[10.5px] text-[#8a6b1f] bg-[#fdf3dd] rounded-md px-[7px] py-[3px]">{t}</span>
                      ))}
                    </div>
                  )}
                </article>
              ))}

              {items.length === 0 && (
                <div className="border border-dashed border-[#d7dae4] rounded-xl px-3 py-[18px] text-center text-[12.5px] text-[#9aa0b2]">
                  Déposez un prospect ici
                </div>
              )}
              {rest > 0 && (
                <div className="text-center font-mono text-[11.5px] text-[#9aa0b2] pt-1 pb-0.5">+ {rest} autres</div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function Kpi({ label, value, tint, color, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#e6e8ee] rounded-2xl px-[18px] py-4 flex items-center gap-[14px]">
      <div className="h-10 w-10 shrink-0 rounded-[11px] flex items-center justify-center" style={{ background: tint }}>
        <Icon size={20} style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="font-mono text-[22px] font-medium tracking-[-.02em]">{value}</div>
        <div className="text-[12.5px] text-[#8a90a3] truncate">{label}</div>
      </div>
    </div>
  );
}
